//@flow

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import get from 'lodash.get';

import classnames from 'classnames';
import { ProgressChart } from 'dibs-trade-rewards-progress';
import RewardsEnvelope from 'dibs-trade-rewards-envelope';

import { Button } from 'dibs-elements/exports/Button';
import styles from './RewardsProgressModule.scss';

import type { RewardsProgressModule_tradeFirm as TradeFirmType } from './__generated__/RewardsProgressModule_tradeFirm.graphql';

type EnvelopeLabelProps = {
    className?: string,
    dollarSignClassName?: string,
    rewardValue: number,
};

class EnvelopeLabel extends React.Component<EnvelopeLabelProps> {
    constructor(props: EnvelopeLabelProps) {
        super(props);
        this.getDigitTokens = this.getDigitTokens.bind(this);
    }

    getDigitTokens: () => Array<string>;

    getDigitTokens() {
        //assumes reward value does not exceed $999
        return this.props.rewardValue
            .toFixed(0)
            .slice(0, 3)
            .split('');
    }

    render() {
        const { className, dollarSignClassName } = this.props;
        const digitTokens = this.getDigitTokens();
        const containerClass =
            digitTokens.length > 2 ? styles.containerThreeDigit : styles.containerTwoDigit;

        return (
            <div className={className ? className : containerClass}>
                <span className={dollarSignClassName ? dollarSignClassName : styles.dollarSign}>
                    $
                </span>
                <span className={styles.verticalOffset1}>{digitTokens[0]}</span>
                <span className={styles.verticalOffset2}>{digitTokens[1]}</span>
                <span className={styles.verticalOffset3}>{digitTokens[2]}</span>
            </div>
        );
    }
}

type RewardsProgressModuleProps = {
    tradeFirm: TradeFirmType,
    wrapperClass?: string,
    registerDeepLink?: (Element, string) => void,
    scrollToBenefitsChart: (e?: SyntheticEvent<*>) => void,
};

const SCROLL_HASH = '#cashBack';

/* eslint-disable */
class RewardsProgressModule extends Component<RewardsProgressModuleProps> {
    componentDidMount() {
        const node = ReactDOM.findDOMNode(this);
        if (node instanceof HTMLElement && this.props.registerDeepLink) {
            this.props.registerDeepLink(node, SCROLL_HASH);
        }
    }

    render() {
        const { props } = this;
        const loyalty = get(props, 'tradeFirm.loyalty');
        const amountSpentTowardsNextReward = Number(get(loyalty, 'nextReward') || 0);
        const amountNeeded = Math.ceil(Number(get(loyalty, 'remainingAmount'))) || 0;
        const rewardValue = Number(get(loyalty, 'nextRewardAmount') || 0);
        const rewardsEarned = Number(get(loyalty, 'rewards') || 0);
        const progressToNextReward = Number(get(loyalty, 'progressToNextReward') || 0) * 100;

        return (
            <div className={classnames(styles.container, props.wrapperClass)}>
                <div className={styles.header} data-tn="cash-back-rewards-header">
                    <FormattedMessage
                        id="rewards.RewardsProgressModule.header"
                        defaultMessage="Cash Back Rewards"
                    />
                </div>
                <div className={styles.bar} />
                <div className={styles.graphicSection}>
                    <div className={styles.chartContainer}>
                        <ProgressChart
                            chartRadius={66}
                            progressBarWidth={6}
                            progressPercentage={progressToNextReward}
                            overlayClass={styles.chartWrapper}
                        >
                            <RewardsEnvelope containerClass={styles.rewardsEnnvelopeContainer}>
                                <EnvelopeLabel rewardValue={rewardValue} />
                            </RewardsEnvelope>
                        </ProgressChart>
                    </div>
                    <div className={styles.chartKey}>
                        <div>
                            <div className={styles.chartKeyLabel}>
                                <FormattedMessage
                                    id="rewards.RewardsProgressModule.amountNeededLabel"
                                    defaultMessage="Points Needed for Next Cash Reward"
                                />
                            </div>
                            <div className={styles.chartKeyValue}>
                                <span
                                    className={styles.chartPointsValue}
                                    data-tn="points-to-next-reward"
                                >
                                    <FormattedNumber
                                        value={amountNeeded}
                                        maximumFractionDigits={0}
                                    />
                                </span>
                            </div>
                        </div>
                        <div>
                            <div className={styles.chartKeyLabel}>
                                <FormattedMessage
                                    id="rewards.RewardsProgressModule.rewardsEarnedLabel"
                                    defaultMessage="Cash Rewards Earned"
                                />
                            </div>
                            <div className={styles.chartPointsValue} data-tn="total-rewards-earned">
                                <FormattedNumber
                                    currencyDisplay="symbol"
                                    currency={'USD'}
                                    maximumFractionDigits={0}
                                    minimumFractionDigits={0}
                                    style="currency"
                                    value={rewardsEarned}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.buttonContainer}>
                    <Button
                        className={styles.aboutCta}
                        onClick={props.scrollToBenefitsChart}
                        size="medium"
                        type="primary"
                        dataTn="trade-rewards-scroll-to-tiers-cta"
                    >
                        <FormattedMessage
                            id="rewards.RewardsProgressModule.aboutCta"
                            defaultMessage="learn about rewards"
                        />
                    </Button>
                </div>
            </div>
        );
    }
}

export { EnvelopeLabel, RewardsProgressModule };

export default createFragmentContainer(RewardsProgressModule, {
    tradeFirm: graphql`
        fragment RewardsProgressModule_tradeFirm on TradeFirmType {
            loyalty {
                rewards
                nextReward
                nextRewardAmount
                remainingAmount
                progressToNextReward
            }
        }
    `,
});
